import _ from 'lodash';
import { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types/';

import {
  SuccessFinancialCovenantGQLType,
  SuccessPortfolioCovenantGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { FacilitiesTableRowData } from './facilitiesTableOptions';
import { useGetBalanceSheetExposureDataQuery } from './getBalanceSheetExposureData.gql.generated';
import {
  GetFacilitiesTableDataQueryGQLType,
  useGetFacilitiesTableDataQuery,
} from './getFacilitiesTableData.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider/useCreditFacilities';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { getDefaultDashboardDisplayStartDate } from '~/packages/common-frontend/constants';
import { hasBorrowingBaseMechanics as hasBorrowingBaseMechanicsUtil } from '~/packages/common-frontend/utils';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { parseInterestRateDisplayForCapitalProvider } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';

const getHasBorrowingBase = ({
  currentCreditAgreementArtifacts,
}: {
  currentCreditAgreementArtifacts: GetFacilitiesTableDataQueryGQLType['organization']['currentCreditAgreementArtifacts'];
}) => {
  return currentCreditAgreementArtifacts.some((creditAgreementFullArtifact) => {
    const parsedCreditAgreement = parseCreditAgreementFullArtifact(
      creditAgreementFullArtifact.creditAgreement,
    );
    if (!parsedCreditAgreement) {
      throw Error(
        `Expect creditAgreementFullArtifact to exist for creditAgreementId ${creditAgreementFullArtifact.id}`,
      );
    }
    const { borrowingBaseMechanics } = parsedCreditAgreement;
    return hasBorrowingBaseMechanicsUtil(borrowingBaseMechanics);
  });
};

/**
 * Given an array of all portcov results for multiple credit facilities
 * Returns the latest covenant evaluation results for each covenantKey if it exists in the latest credit agreement
 */
const getLatestCovenantResultsForCreditFacility = ({
  allCovenantResults,
  latestCreditAgreement,
  covenantType,
}: {
  allCovenantResults: (
    | SuccessFinancialCovenantGQLType
    | SuccessPortfolioCovenantGQLType
  )[];
  latestCreditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  covenantType: 'financial' | 'portfolio';
}) => {
  const creditFacilityId = latestCreditAgreement.creditFacilityId;

  const allCurrentCovenantKeysFromCreditAgreement: Set<string> =
    covenantType === 'portfolio'
      ? new Set(
          latestCreditAgreement.portfolioCovenants.map(
            (portCov) => portCov.covenantKey,
          ),
        )
      : new Set(
          latestCreditAgreement.financialCovenants.map(
            (finCov) => finCov.covenantKey,
          ),
        );

  const latestVersionCovenantResultsForCreditFacility = allCovenantResults.reduce(
    (acc, covenantResult) => {
      const covenantKey = covenantResult.covenantKey;

      // filter first by creditFacilityId and covenants that exist in the latest credit agreement
      if (
        covenantResult.creditFacilityId === creditFacilityId &&
        allCurrentCovenantKeysFromCreditAgreement.has(covenantKey)
      ) {
        // if the covenantKey is not yet in the acc,
        // or if the current result has a newer reportingDate,
        // or the same reportingDate but a higher version,
        // update the acc
        if (
          !acc[covenantKey] ||
          covenantResult.reportingDate > acc[covenantKey].reportingDate ||
          (covenantResult.reportingDate === acc[covenantKey].reportingDate &&
            covenantResult.version > acc[covenantKey].version)
        ) {
          // eslint-disable-next-line no-param-reassign
          acc[covenantKey] = covenantResult;
        }
      }
      return acc;
    },
    {} as Record<
      string,
      SuccessPortfolioCovenantGQLType | SuccessFinancialCovenantGQLType
    >,
  );

  return Object.values(latestVersionCovenantResultsForCreditFacility);
};

const computeTotalsRow = (creditFacilityRows: FacilitiesTableRowData[]) => {
  const totalsRow = creditFacilityRows.reduce(
    (acc, row) => ({
      ...acc,
      globalCommitmentAmount:
        acc.globalCommitmentAmount + row.globalCommitmentAmount,
      globalLoanOutstanding:
        acc.globalLoanOutstanding + row.globalLoanOutstanding,
      globalUnusedCommitment:
        acc.globalUnusedCommitment + row.globalUnusedCommitment,
      capitalProviderCommitmentAmount:
        acc.capitalProviderCommitmentAmount +
        row.capitalProviderCommitmentAmount,
      capitalProviderLoanOutstanding:
        acc.capitalProviderLoanOutstanding + row.capitalProviderLoanOutstanding,
      capitalProviderUnused:
        acc.capitalProviderUnused + row.capitalProviderUnused,
      collectionsAccountBalance:
        acc.collectionsAccountBalance + row.collectionsAccountBalance,
      requiredReserve: acc.requiredReserve + row.requiredReserve,
      totalBorrowingBase: acc.totalBorrowingBase + row.totalBorrowingBase,
      borrowingBaseFromReceivables:
        acc.borrowingBaseFromReceivables + row.borrowingBaseFromReceivables,
      borrowingBaseCashContribution:
        acc.borrowingBaseCashContribution + row.borrowingBaseCashContribution,
      accruedInterestAndFees:
        acc.accruedInterestAndFees + row.accruedInterestAndFees,
      financialCovenantsFailed: !_.isNil(row.financialCovenantsFailed)
        ? (acc.financialCovenantsFailed ?? 0) + row.financialCovenantsFailed
        : acc.financialCovenantsFailed,
      portfolioCovenantsFailed: !_.isNil(row.portfolioCovenantsFailed)
        ? (acc.portfolioCovenantsFailed ?? 0) + row.portfolioCovenantsFailed
        : acc.portfolioCovenantsFailed,
    }),
    {
      creditFacilityName: 'Portfolio totals',
      externalFacilityId: null,
      globalCommitmentAmount: 0,
      globalLoanOutstanding: 0,
      globalUnusedCommitment: 0,
      globalUtilization: 0,
      capitalProviderCommitmentAmount: 0,
      capitalProviderLoanOutstanding: 0,
      capitalProviderUnused: 0,
      capitalProviderUtilization: 0,
      collectionsAccountBalance: 0,
      requiredReserve: 0,
      totalBorrowingBase: 0,
      borrowingBaseFromReceivables: 0,
      borrowingBaseCashContribution: 0,
      accruedInterestAndFees: 0,
      interestRates: '-',
      dateOfLastFundingRequest: undefined,
      financialCovenantsFailed: null,
      portfolioCovenantsFailed: null,
    },
  );

  // Utilization based on totals
  totalsRow.globalUtilization =
    totalsRow.globalCommitmentAmount > 0
      ? totalsRow.globalLoanOutstanding / totalsRow.globalCommitmentAmount
      : 0;
  totalsRow.capitalProviderUtilization =
    totalsRow.capitalProviderCommitmentAmount > 0
      ? totalsRow.capitalProviderLoanOutstanding /
        totalsRow.capitalProviderCommitmentAmount
      : 0;

  return totalsRow;
};

const mapQueryDataToTableRows = ({
  organizationId,
  creditFacilities,
  facilitiesTableQueryData: {
    organization: { currentCreditAgreementArtifacts },
    fundingRequests,
    expensesFeesLedgerForAllCountersigners,
    outstandingLedgerForAllCountersigners,
    outstandingLedgerForCapitalProvider,
    latestBorrowingBaseReports,
    getSuccessPortfolioCovenantsByCreditFacilityIds,
    getSuccessFinancialCovenantsByCreditFacilityIds,
  },
}: {
  organizationId: string;
  creditFacilities: CreditFacility[];
  facilitiesTableQueryData: GetFacilitiesTableDataQueryGQLType;
}): FacilitiesTableRowData[] => {
  const creditAgreements = currentCreditAgreementArtifacts.map(
    (creditAgreementFullArtifact) => {
      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementFullArtifact.creditAgreement,
      );
      if (!parsedCreditAgreement) {
        throw Error(
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${creditAgreementFullArtifact.id}`,
        );
      }
      return parsedCreditAgreement;
    },
  );

  // Bucket data from all of the different queries by credit facility
  const creditFacilitiesWithData = creditFacilities.map((creditFacility) => {
    const lastCreditAgreement = creditAgreements.find(
      (obj) => obj.creditFacilityId === creditFacility.id,
    );
    if (!lastCreditAgreement) {
      throw Error('Missing "lastCreditAgreement" in facilities table');
    }

    const latestPortfolioCovenantResultsForCreditFacility = getLatestCovenantResultsForCreditFacility(
      {
        allCovenantResults: getSuccessPortfolioCovenantsByCreditFacilityIds,
        latestCreditAgreement: lastCreditAgreement,
        covenantType: 'portfolio',
      },
    );

    const latestFinancialCovenantResultsForCreditFacility = getLatestCovenantResultsForCreditFacility(
      {
        allCovenantResults: getSuccessFinancialCovenantsByCreditFacilityIds,
        latestCreditAgreement: lastCreditAgreement,
        covenantType: 'financial',
      },
    );

    return {
      creditFacility,
      lastCreditAgreement,
      lastFundingRequest: _.orderBy(
        fundingRequests.nodes.filter(
          (obj) => obj.creditFacilityId === creditFacility.id,
        ),
        'created_at',
        'desc',
      )[0] as
        | GetFacilitiesTableDataQueryGQLType['fundingRequests']['nodes'][number]
        | undefined,
      expensesFeesLedgerForFacilityForAllCountersigners: expensesFeesLedgerForAllCountersigners.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      outstandingLedgerForFacilityForAllCountersigners: outstandingLedgerForAllCountersigners.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      outstandingLedgerForFacilityForCapitalProvider: outstandingLedgerForCapitalProvider.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      portfolioCovenantsForFacility: latestPortfolioCovenantResultsForCreditFacility,
      financialCovenantsForFacility: latestFinancialCovenantResultsForCreditFacility,
      borrowingBaseReport: latestBorrowingBaseReports.find(
        (obj) => obj?.creditFacilityId === creditFacility.id,
      ),
    };
  });

  // Map credit facility data to table row derived values
  const creditFacilityRows = creditFacilitiesWithData.map(
    ({
      creditFacility,
      lastCreditAgreement,
      lastFundingRequest,
      expensesFeesLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForCapitalProvider,
      portfolioCovenantsForFacility,
      financialCovenantsForFacility,
      borrowingBaseReport,
    }): FacilitiesTableRowData => {
      const globalCommitmentAmount = _.sumBy(
        lastCreditAgreement.facilityMechanics.parties.capitalProviders,
        (capitalProviderParty) =>
          capitalProviderParty.commitmentAmount.value ?? 0,
      );
      const globalLoanOutstanding =
        outstandingLedgerForFacilityForAllCountersigners?.ledger.balance ?? 0;
      return {
        // "Facility"
        creditFacilityName: creditFacility.name,
        externalFacilityId:
          lastCreditAgreement.facilityMechanics.externalFacilityId?.value ??
          null,

        // "Global Commitment Amount"
        globalCommitmentAmount: globalCommitmentAmount ?? 0,
        // "Global Outstanding Balance"
        globalLoanOutstanding,
        // "Global Unused Commitment"
        globalUnusedCommitment:
          (globalCommitmentAmount ?? 0) -
          (outstandingLedgerForFacilityForAllCountersigners?.ledger.balance ??
            0),
        // "Global Utilization"
        globalUtilization:
          globalCommitmentAmount > 0
            ? globalLoanOutstanding / globalCommitmentAmount
            : 0,

        // Capital provider-only cells. These are hidden for borrowers.
        ...(() => {
          const capitalProviderCommitmentAmount =
            lastCreditAgreement.facilityMechanics.parties.capitalProviders.find(
              (cp) => cp.entity.id === organizationId,
            )?.commitmentAmount.value ?? 0;
          const capitalProviderLoanOutstanding =
            outstandingLedgerForFacilityForCapitalProvider?.ledger.balance ?? 0;
          const capitalProviderUnused =
            capitalProviderCommitmentAmount - capitalProviderLoanOutstanding;

          return {
            // "Capital Provider Commitment Amount"
            capitalProviderCommitmentAmount,
            // "Capital Provider Outstanding Balance"
            capitalProviderLoanOutstanding,
            // "Capital Provider Unused Commitment"
            capitalProviderUnused,
            // "Capital Provider Utilization"
            capitalProviderUtilization:
              capitalProviderCommitmentAmount > 0
                ? capitalProviderLoanOutstanding /
                  capitalProviderCommitmentAmount
                : 0,
          };
        })(),

        // "Collections Account Balance"
        collectionsAccountBalance:
          borrowingBaseReport?.totalCollectionsAccountCash ?? 0,
        // "Required reserve"
        requiredReserve: borrowingBaseReport?.borrowingBaseReserveAmount ?? 0,
        // "Total Borrowing Base"
        // !TODO @danitian Remove derivation during borrowing base migration phase 3
        totalBorrowingBase:
          (borrowingBaseReport?.totalCashBorrowingBaseAmount ?? 0) +
          (borrowingBaseReport?.maxReceivableAdvanceAmount ?? 0),
        // "Borrowing Base from Receivables"
        borrowingBaseFromReceivables:
          borrowingBaseReport?.maxReceivableAdvanceAmount ?? 0,
        // "Borrowing Base from Cash"
        borrowingBaseCashContribution:
          borrowingBaseReport?.totalCashBorrowingBaseAmount ?? 0,
        // "Accrued Interest and Fees"
        accruedInterestAndFees:
          expensesFeesLedgerForFacilityForAllCountersigners?.ledger.balance ??
          0,
        // "Interest Rates"
        interestRates: lastCreditAgreement.facilityMechanics.parties.capitalProviders
          .map((capitalProvider) => {
            const rate = parseInterestRateDisplayForCapitalProvider(
              capitalProvider,
            );
            return `${capitalProvider.entity.name}: ${rate}`;
          })
          .join(', '),
        // "Date of Last Funding Request"
        dateOfLastFundingRequest: lastFundingRequest
          ? commonDate.epochMsToFullDateStr(lastFundingRequest.created_at)
          : undefined,
        // "Portfolio Covenants"
        // null value will be rendered as '-'
        portfolioCovenantsFailed: portfolioCovenantsForFacility.length
          ? _.sumBy(
              portfolioCovenantsForFacility,
              (portfolioCovenantResult) => {
                if (portfolioCovenantResult.result !== 'PASS') {
                  return 1;
                }
                return 0;
              },
            )
          : null,
        // "Financial Covenants"
        // null value will be rendered as '-'
        financialCovenantsFailed: financialCovenantsForFacility.length
          ? _.sumBy(
              financialCovenantsForFacility,
              (financialCovenantResult) => {
                if (financialCovenantResult.result !== 'PASS') {
                  return 1;
                }
                return 0;
              },
            )
          : null,
      };
    },
  );
  return creditFacilityRows;
};

// Exports
export const useFacilitiesTableData = () => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();
  const organizationId = user.organizationId;

  // Get credit facilities for this org
  const { facilities: creditFacilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = creditFacilities.map(({ id }) => id);

  // Get the credit facility details that we can query in aggregate
  // TODO @sachinr4 - look into consolidating accounting calculations and aggregations used in this query (eg. outstanding balance)
  const facilitiesTableQuery = useGetFacilitiesTableDataQuery({
    // No credit facility IDs, most likely waiting on the credit facility query
    skip: creditFacilityIds.length === 0,
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityIds,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      organizationId,
      startDate: commonDate.fullDateStrToEpoch(
        getDefaultDashboardDisplayStartDate(),
      ),
      startDateStr: getDefaultDashboardDisplayStartDate(),
    },
  });

  const hasBorrowingBase = useMemo(
    () =>
      facilitiesTableQuery.data
        ? getHasBorrowingBase(facilitiesTableQuery.data.organization)
        : false,
    [facilitiesTableQuery.data],
  );

  const { computedRows, totalsRow } = useMemo(() => {
    const computed = facilitiesTableQuery.data
      ? mapQueryDataToTableRows({
          creditFacilities,
          facilitiesTableQueryData: facilitiesTableQuery.data,
          organizationId,
        })
      : [];

    return {
      hasBorrowingBase,
      computedRows: computed,
      totalsRow: computeTotalsRow(computed),
    };
  }, [
    creditFacilities,
    facilitiesTableQuery.data,
    organizationId,
    hasBorrowingBase,
  ]);

  const balanceSheetExposureDataQuery = useGetBalanceSheetExposureDataQuery({
    variables: { organizationId },
    fetchPolicy: 'network-only',
  });

  const balanceSheetImpactCalcs = balanceSheetExposureDataQuery.data ?? null;

  return {
    hasBorrowingBase,
    computedRows,
    creditFacilities,
    totalsRow,
    balanceSheetImpactCalcs,
    error: facilitiesTableQuery.error || balanceSheetExposureDataQuery.error,
    loading:
      facilitiesTableQuery.loading ||
      !facilitiesTableQuery.data ||
      balanceSheetExposureDataQuery.loading ||
      !balanceSheetExposureDataQuery.data,
  };
};
